// ARCHIVO COMPARTIDO app/web — editar en ambos repos. Verificar con `npm run check:shared` (TemControlApp).

export const USER_TYPES = {
  WORKER: 0,
  COMPANY_ADMIN: 1,
  SURROGATE_ADMIN: 2,
  OVERSEER: 3,
  PUNISHER: 4,
  MECHAGODZILLA: 5,
};

export const USER_TYPE_LABELS = {
  [USER_TYPES.WORKER]: 'Trabajador',
  [USER_TYPES.COMPANY_ADMIN]: 'Admin empresa',
  [USER_TYPES.SURROGATE_ADMIN]: 'Admin sede',
  [USER_TYPES.OVERSEER]: 'Supervisor',
  [USER_TYPES.PUNISHER]: 'Inspector SS',
  [USER_TYPES.MECHAGODZILLA]: 'Superadmin',
};

const RULES = {
  // Lista global de empresas: sólo inspector y superadmin.
  viewEmpresas:    [4, 5],
  // Crear / borrar empresas: sólo superadmin.
  manageEmpresas:  [5],
  // Entrar al detalle de una empresa: admin empresa y admin sede sólo a la suya
  // (eso lo enforce CompanyDetailPage); inspector y super entran a cualquiera.
  viewOwnCompany:  [1, 2, 4, 5],
  // Editar datos de la empresa: admin empresa (su propia empresa) + super.
  manageOwnCompany: [1, 5],
  // Crear / editar sedes: admin empresa + super. (Borrar sólo super, ver deleteWorkplace.)
  manageWorkplaces: [1, 5],
  // Borrar sedes: sólo super.
  deleteWorkplace:  [5],
  viewWorkers:    [1, 2, 3, 4, 5],
  manageWorkers:  [1, 2, 5],
  viewReports:    [0, 1, 2, 3, 4, 5],
  // Generar informes descargables (PDF/Excel). Todos los roles tienen acceso,
  // pero el scope visible varía: trabajador y supervisor sólo lo SUYO; admin
  // sede su sede; admin empresa su empresa entera; inspector y super todas.
  generateReports: [0, 1, 2, 3, 4, 5],
  fichar:         [0, 1, 2, 5],
  viewEquipo:     [1, 2, 3, 4, 5],
  approveEvents:  [1, 2, 5],
  manageAgreements: [5],
  // Gestion del calendario nacional/CCAA de festivos: solo superadmin.
  manageHolidays: [5],
  // Festivos de EMPRESA (importar Excel / borrar): admin empresa (su propia
  // empresa, lo enforce la página y el backend) + super (cualquiera).
  manageCompanyHolidays: [1, 5],
  // Listado de documentos por scope:
  //  - super (5): todos los documentos del sistema.
  //  - inspector (4): todos los documentos del scope que tenga (toda
  //    su empresa, o todas si no esta asignado a ninguna).
  //  - admin empresa (1): documentos de su empresa, todas las sedes.
  //  - admin sede (2): documentos de su sede unicamente.
  //  - supervisor (3) y trabajador (0): SOLO sus propios uploads.
  // Quien tiene este permiso ve mas que sus propios documentos; los demas
  // estan limitados a uploaderId === user.id en DocumentosPage.
  viewAllDocuments: [1, 2, 4, 5],
  bulkImport: [5],
};

export const can = (userType, action) =>
  (RULES[action] || []).includes(userType ?? -1);

// Los roles de sede (admin sede, overseer) sólo pueden ver/gestionar
// trabajadores de su mismo workingSite. Admins de empresa, inspector
// y superadmin NO están restringidos a sede.
export const isSedeScoped = (userType) => userType === 2 || userType === 3;

// ── Multicentro (fichaje multicentro) ──────────────────────────────────────
// Un trabajador puede estar asignado a VARIOS centros. Estos helpers
// centralizan la lógica para no repetir comparaciones sueltas por todo el
// código y mantener la compatibilidad con el modelo antiguo (1 sede).

// Array de IDs de centro (string) de un trabajador. Si el backend aún no manda
// `workingSites`, se deriva de [workingSite]. Filtra nulos.
export const workerSites = (worker) => {
  if (!worker) return [];
  const arr = Array.isArray(worker.workingSites) ? worker.workingSites : null;
  const src = (arr && arr.length)
    ? arr
    : (worker.workingSite != null ? [worker.workingSite] : []);
  return src.filter((s) => s != null).map((s) => String(s));
};

// ¿El trabajador está asignado a este centro?
export const workerInSite = (worker, siteId) =>
  siteId != null && workerSites(worker).includes(String(siteId));

// Centro de referencia del usuario (primario) para el scope de admin de sede.
export const primarySite = (user) => {
  if (!user) return null;
  if (user.primaryWorkplaceId != null) return String(user.primaryWorkplaceId);
  if (user.workingSite != null) return String(user.workingSite);
  const sites = workerSites(user);
  return sites.length ? sites[0] : null;
};

// Reduce un listado de trabajadores al scope visible por el usuario.
// Mantiene siempre al propio user (para evitar esconder su propia ficha).
export const filterWorkersForUser = (workers, user) => {
  if (!user || !Array.isArray(workers)) return workers || [];
  if (!isSedeScoped(user.type)) return workers;
  const target = primarySite(user);
  if (!target) return workers; // sin sede asignada: no restringimos
  // Multicentro: incluye al trabajador si SU centro primario está entre los
  // centros asignados del otro (workerInSite cubre el array y el legacy).
  return workers.filter((w) => workerInSite(w, target) || w.id === user.id);
};

// ── Roles asignables al crear/editar un trabajador ──────────────────────────
// Regla general: nunca puedes asignar un rol con privilegios mayores a los tuyos.
//  - super (5) puede asignar cualquiera.
//  - admin empresa (1) puede asignar trabajador (0), ADMIN EMPRESA (1) y
//    admin sede (2) — siempre dentro de SU empresa, para que pueda delegar
//    la administración sin depender del superadmin.
//    NO puede asignar supervisor (3), inspector (4) ni super (5), que son
//    roles con alcance fuera de la empresa.
//  - admin sede (2) sólo puede asignar trabajador (0) — siempre en su sede.
//  - inspector (4), supervisor (3) y trabajador (0) no crean usuarios.
const ASSIGNABLE_ROLES = {
  5: [0, 1, 2, 3, 4, 5],
  1: [0, 1, 2],
  2: [0],
};

export const assignableRoles = (creatorType) => ASSIGNABLE_ROLES[creatorType] || [];

export const canAssignRole = (creatorType, targetType) =>
  assignableRoles(creatorType).includes(targetType);

// Valida si un usuario puede crear/editar a otro completo (rol + scope).
// No comprueba la operación en sí (creación vs edición), sólo el alcance.
export const canManageWorker = (creator, target) => {
  if (!creator || !target) return false;
  if (!can(creator.type, 'manageWorkers')) return false;
  if (!canAssignRole(creator.type, target.type ?? 0)) return false;
  // super: sin restricciones de empresa/sede
  if (creator.type === 5) return true;
  // admin empresa: misma empresa
  if (creator.type === 1) {
    return Number(creator.companyId) === Number(target.companyId);
  }
  // admin sede: misma empresa y misma sede
  if (creator.type === 2) {
    return Number(creator.companyId) === Number(target.companyId)
      && workerInSite(target, primarySite(creator));
  }
  return false;
};

// ── Eliminación de fichajes ───────────────────────────────────────────────
// Solo admin empresa (1), admin sede (2) y super (5) pueden borrar fichajes.
// Trabajador (0), supervisor (3) e inspector (4) NO — el trabajador debe pedir
// corrección al admin que corresponda.
// Scope:
//   - super (5): cualquier evento.
//   - admin empresa (1): eventos de trabajadores de su empresa (incluye los
//     propios, ya que el admin es trabajador de su propia empresa).
//   - admin sede (2): eventos de trabajadores de su misma empresa Y sede.
// `eventOwner` es el worker dueño del evento (con companyId y workingSite).
export const canDeleteEvent = (user, eventOwner) => {
  if (!user || !eventOwner) return false;
  if (user.type === 5) return true;
  if (user.type === 1) {
    return Number(user.companyId) === Number(eventOwner.companyId);
  }
  if (user.type === 2) {
    return Number(user.companyId) === Number(eventOwner.companyId)
      && workerInSite(eventOwner, primarySite(user));
  }
  return false;
};

// ── Mensajería: quién puede iniciar conversación con quién ─────────────────
// Reglas (decisión de producto):
//   - super (5) e inspector (4): a cualquiera.
//   - admin empresa (1): a usuarios de su misma empresa.
//   - admin sede (2): a admin empresa de su empresa, a otros admin sede de
//     la misma empresa, y a trabajadores/supervisores de SU sede.
//   - trabajador (0) y supervisor (3): SOLO a SU admin empresa o a SU
//     admin sede. NO a otros trabajadores. NO a super ni a inspector.
export const canMessage = (sender, receiver) => {
  if (!sender || !receiver) return false;
  if (sender.id === receiver.id) return false;
  const s = sender.type;
  const r = receiver.type;
  if (s === 5) return true;
  if (s === 4) return true;
  if (s === 1) {
    return Number(sender.companyId) === Number(receiver.companyId);
  }
  if (s === 2) {
    if (Number(sender.companyId) !== Number(receiver.companyId)) return false;
    if (r === 1 || r === 2) return true; // a admins de la empresa
    // a trabajadores / supervisores: sólo si el centro del admin de sede está
    // entre los centros asignados del receptor (multicentro).
    return workerInSite(receiver, primarySite(sender));
  }
  if (s === 0 || s === 3) {
    // Trabajador o supervisor: solo a SU admin empresa (mismo companyId) o
    // a SU admin sede (mismo companyId + mismo workingSite).
    if (Number(sender.companyId) !== Number(receiver.companyId)) return false;
    if (r === 1) return true; // admin empresa de la propia empresa
    if (r === 2) {
      // El trabajador puede escribir a un admin de sede si el centro de ese
      // admin está entre los centros asignados del trabajador (multicentro).
      return workerInSite(sender, primarySite(receiver));
    }
    return false;
  }
  return false;
};

// Filtra una lista de candidatos a destinatarios.
export const filterReceivers = (sender, candidates) => {
  if (!sender || !Array.isArray(candidates)) return [];
  return candidates.filter((c) => canMessage(sender, c));
};

// ── Mensajería colectiva: ¿qué envíos masivos puede hacer este usuario? ────
//   Devuelve un objeto:
//     { workplace: 'own' | 'any' | null, company: 'own' | 'any' | null,
//       allCompanies: true | false }
//   - 'own'         → fijo a su propia empresa/sede (sin selector).
//   - 'any'         → puede elegir cualquier empresa/sede (selector).
//   - null          → no puede hacer ese tipo de envío.
//   - allCompanies  → enviar a TODOS los usuarios del sistema (solo super).
export const broadcastScopes = (sender) => {
  if (!sender) return { workplace: null, company: null, allCompanies: false };
  switch (sender.type) {
    case 5: // super: cualquiera + todas las empresas a la vez
      return { workplace: 'any', company: 'any', allCompanies: true };
    case 1: // admin empresa: solo su empresa entera
      return { workplace: null, company: 'own', allCompanies: false };
    case 2: // admin sede: solo su sede
      return { workplace: 'own', company: null, allCompanies: false };
    default:
      return { workplace: null, company: null, allCompanies: false };
  }
};

export const canBroadcast = (sender) => {
  const s = broadcastScopes(sender);
  return s.workplace !== null || s.company !== null || s.allCompanies;
};

export const decodeJWT = (token) => {
  try {
    const payload = token.split('.')[1];
    // base64url -> base64 estándar (rellenamos = si hace falta).
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(
      payload.length + (4 - (payload.length % 4)) % 4, '='
    );
    // atob devuelve una cadena binaria (un char por byte). Si hubo
    // caracteres UTF-8 multi-byte (acentos, ñ), atob por sí solo los
    // rompe — hay que reinterpretar los bytes como UTF-8.
    const binary = atob(b64);
    let utf8;
    try {
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      utf8 = new TextDecoder('utf-8').decode(bytes);
    } catch {
      // Fallback para entornos sin TextDecoder.
      utf8 = decodeURIComponent(binary.split('').map((c) =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join(''));
    }
    return JSON.parse(utf8);
  } catch {
    return null;
  }
};
